/**
 * Real-time Notifications Hook
 * Subscribes to Pusher events for live notification updates
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import type { BackendNotification, NotificationApp } from '../types';
import { notificationKeys } from './notificationKeys';

// Type for Pusher channel
interface PusherChannel {
  bind: (event: string, callback: (data: unknown) => void) => void;
  unbind: (event: string, callback: (data: unknown) => void) => void;
}

// Type for incoming Pusher notification payload
interface PusherNotificationPayload {
  id?: string;
  key: string;
  title: string;
  description?: string | null;
  notification_ts?: string | Date;
  payload?: Record<string, unknown> | null;
  entity_id?: string | null;
  entity_type?: string | null;
  recipient_id: string;
  recipient_type: string;
  sender_id?: string | null;
  sender_type?: string | null;
  app?: NotificationApp | null;
  category?: BackendNotification['category'];
  created_at?: string | Date;
  updated_at?: string | Date;
}

interface UseRealtimeNotificationsOptions {
  userId: string;
  pusherHooks: {
    subscribe: (channel: string) => PusherChannel;
    unsubscribe: (channel: string) => void;
  };
  channelName: string;
  events: string[];
  onNotification?: (notification: PusherNotificationPayload) => void;
}

/**
 * Hook to subscribe to real-time notification events
 * Automatically updates React Query cache when new notifications arrive
 */
export function useRealtimeNotifications({
  userId,
  pusherHooks,
  channelName,
  events,
  onNotification,
}: UseRealtimeNotificationsOptions) {
  const { subscribe, unsubscribe } = pusherHooks;
  const queryClient = useQueryClient();

  const handleNotificationReceived = useCallback(
    (data: PusherNotificationPayload) => {
      // Invalidate and refetch notification list queries
      queryClient.invalidateQueries({
        queryKey: notificationKeys.lists(),
      });

      // Invalidate and refetch unread count (all apps)
      queryClient.invalidateQueries({
        queryKey: notificationKeys.unreadCount(),
      });

      // Also invalidate app-specific unread count if app is specified
      if (data.app) {
        queryClient.invalidateQueries({
          queryKey: notificationKeys.unreadCount(data.app),
        });
      }

      // Call optional callback for toast display, etc.
      onNotification?.(data);
    },
    [queryClient, onNotification]
  );

  useEffect(() => {
    if (!userId) {
      return;
    }

    // Subscribe to user's private channel
    const channel = subscribe(channelName);

    // Bind all event listeners
    const boundHandlers = events.map((event) => {
      const handler = (data: unknown) =>
        handleNotificationReceived(data as PusherNotificationPayload);
      channel.bind(event, handler);
      return { event, handler };
    });

    // Cleanup on unmount
    return () => {
      boundHandlers.forEach(({ event, handler }) => {
        channel.unbind(event, handler);
      });
      unsubscribe(channelName);
    };
  }, [userId, subscribe, unsubscribe, channelName, events, handleNotificationReceived]);
}
